import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  monthlyProcessApi,
  MONTH_OPTIONS,
  YEAR_OPTIONS,
  GENDER_OPTIONS,
  STATUS_OPTIONS,
  TOOLS_OPTIONS,
  MONTHLY_ATTENDANCE_PREDEFINED_REPORTS,
  getMonthName,
} from '../api/monthlyProcessApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';
import { useExportJob } from '../../../../hooks/useExportJob';
import { executePrintReport } from '../../../../utils/printReportUtil';

// Default report column definitions (used for print preview)
const REPORT_COLUMNS_MAP = {
  monthly_attendance_summary: [
    { key: 'Company', label: 'Company' },
    { key: 'Branch', label: 'Branch' },
    { key: 'Department', label: 'Department' },
    { key: 'EmpId', label: 'Emp ID' },
    { key: 'emp_name', label: 'Employee Name' },
    { key: 'MonthName', label: 'Month' },
    { key: 'Year', label: 'Year' },
    { key: 'PresentDays', label: 'Present Days' },
    { key: 'AbsentDays', label: 'Absent Days' },
  ],
  monthly_payable_days: [
    { key: 'Company', label: 'Company' },
    { key: 'Branch', label: 'Branch' },
    { key: 'Department', label: 'Department' },
    { key: 'EmpId', label: 'Emp ID' },
    { key: 'emp_name', label: 'Employee Name' },
    { key: 'MonthName', label: 'Month' },
    { key: 'Year', label: 'Year' },
    { key: 'TotalDays', label: 'Total Days' },
    { key: 'PayableDays', label: 'Payable Days' },
    { key: 'AbsentDays', label: 'Absent Days' },
  ],
  monthly_emp_attendance_history: [
    { key: 'EmpID', label: 'Emp ID' },
    { key: 'Name', label: 'Employee Name' },
    { key: 'Description', label: 'Description' },
    { key: 'ADate', label: 'Date' },
    { key: 'ADayName', label: 'Day' },
    { key: 'FirstSwipe', label: 'First Swipe' },
    { key: 'LastSwipe', label: 'Last Swipe' },
    { key: 'ManualPunch', label: 'Manual Punch' },
    { key: 'Attendance', label: 'Attendance' },
    { key: 'Remarks', label: 'Remarks' },
  ],
};

export function useMonthlyProcessList() {
  const today = useMemo(() => new Date(), []);

  // Filter states — numeric IDs for combobox, 'All' for dropdowns
  const [companyFilter, setCompanyFilter] = useState(0);
  const [branchFilter, setBranchFilter] = useState(0);
  const [departmentFilter, setDepartmentFilter] = useState(0);
  const [employeeFilter, setEmployeeFilter] = useState(0);
  const [empGroupFilter, setEmpGroupFilter] = useState(0);
  const [genderFilter, setGenderFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [monthFilter, setMonthFilter] = useState(today.getMonth() + 1);
  const [yearFilter, setYearFilter] = useState(today.getFullYear());
  const [searchText, setSearchText] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  // Records
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Dynamic dropdown options loaded from backend
  const [options, setOptions] = useState({
    companies: [{ value: 0, label: 'All Companies' }],
    branches: [{ value: 0, label: 'All Branches' }],
    departments: [{ value: 0, label: 'All Departments' }],
    groups: [{ value: 0, label: 'All Groups' }],
    genders: GENDER_OPTIONS,
  });

  // PredefinedReportsDrawer state
  const [isReportsDrawerOpen, setIsReportsDrawerOpen] = useState(false);

  // Background export job via JobEngine
  const { triggerExport, isExporting } = useExportJob({
    module: 'monthly_process',
    jobType: 'EXPORT',
    fileFormat: 'XLSX',
  });

  // Active filters memo for passing to report actions and exports
  const activeFilters = useMemo(
    () => ({
      month: monthFilter,
      year: yearFilter,
      companyId: companyFilter || 0,
      branchId: branchFilter || 0,
      deptId: departmentFilter || 0,
      empId: employeeFilter || 0,
      empGroupId: empGroupFilter || 0,
      gender: genderFilter,
      status: statusFilter,
      search: searchText,
    }),
    [monthFilter, yearFilter, companyFilter, branchFilter, departmentFilter, employeeFilter, empGroupFilter, genderFilter, statusFilter, searchText]
  );

  // Load dynamic filter options from backend once
  useEffect(() => {
    monthlyProcessApi
      .getFilterOptions()
      .then((data) => {
        if (data) {
          setOptions({
            companies: data.companies || [{ value: 0, label: 'All Companies' }],
            branches: data.branches || [{ value: 0, label: 'All Branches' }],
            departments: data.departments || [{ value: 0, label: 'All Departments' }],
            groups: data.groups || [{ value: 0, label: 'All Groups' }],
            genders: data.genders || GENDER_OPTIONS,
          });
        }
      })
      .catch(() => {
        // Silently fall back to defaults
      });
  }, []);

  // Load records
  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await monthlyProcessApi.getRecords({ ...activeFilters, page, pageSize });
      setRecords(data.items);
      setTotalCount(data.total_count);
      setTotalPages(data.total_pages);
    } catch (err) {
      appToast.error('Failed to load monthly attendance records.');
    } finally {
      setIsLoading(false);
    }
  }, [activeFilters, page, pageSize]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  const resetFilters = () => {
    setCompanyFilter(0);
    setBranchFilter(0);
    setDepartmentFilter(0);
    setEmployeeFilter(0);
    setEmpGroupFilter(0);
    setGenderFilter('All');
    setStatusFilter('All');
    setMonthFilter(today.getMonth() + 1);
    setYearFilter(today.getFullYear());
    setSearchText('');
    setPage(1);
  };

  const handleStatusTabChange = (key) => {
    setStatusFilter(key);
    setPage(1);
  };

  // ---------------------------------------------------------------------------
  // DELETE
  // ---------------------------------------------------------------------------

  const handleDelete = async (id, empName) => {
    const confirmed = await appConfirm({
      title: 'Delete Monthly Attendance Record',
      message: `Are you sure you want to delete the monthly attendance summary for ${empName}?`,
      confirmText: 'Delete Record',
      variant: 'danger',
      icon: 'delete',
    });
    if (!confirmed) return;
    try {
      await monthlyProcessApi.deleteRecord(id);
      appToast.success('Monthly attendance record deleted successfully.');
      loadRecords();
    } catch {
      appToast.error('Failed to delete monthly attendance record.');
    }
  };

  // ---------------------------------------------------------------------------
  // REPORT ACTION HANDLER (PredefinedReportsDrawer)
  // Mirrors useDailyProcessList.handleReportAction pattern
  // ---------------------------------------------------------------------------

  const handleReportAction = async (actionKey, reportKey) => {
    if (actionKey === 'print') {
      // Get default columns for this report
      let repCols = REPORT_COLUMNS_MAP[reportKey] || REPORT_COLUMNS_MAP.monthly_attendance_summary;
      const reportDef = MONTHLY_ATTENDANCE_PREDEFINED_REPORTS.find((r) => r.reportKey === reportKey);
      const repTitle = reportDef?.title || 'Monthly Attendance Report';

      // Apply saved column preferences from ReportColumnsModal (localStorage)
      const savedColsJson = localStorage.getItem(`report_cols_${reportKey}`);
      if (savedColsJson) {
        try {
          const savedCols = JSON.parse(savedColsJson);
          const visibleSaved = savedCols.filter((c) => c.visible);
          if (visibleSaved.length > 0) {
            const reordered = [];
            visibleSaved.forEach((sc) => {
              const matched = repCols.find((rc) => rc.label === sc.label || rc.key === sc.key);
              if (matched) reordered.push(matched);
            });
            if (reordered.length > 0) repCols = reordered;
          }
        } catch (e) {
          // Fallback to default columns
        }
      }

      const monthLabel = getMonthName(monthFilter);
      executePrintReport({
        title: `${repTitle} — ${monthLabel} ${yearFilter}`,
        columns: repCols,
        metadata: {
          Month: monthLabel,
          Year: String(yearFilter),
          Report: repTitle,
          'Status Filter': statusFilter || 'All',
        },
        fetchReportData: async () => {
          const res = await monthlyProcessApi.fetchReportData(reportKey, activeFilters);
          return { items: res.items || [] };
        },
        onExportPdf: () => triggerExport({ ...activeFilters, report_key: reportKey }, 'PDF'),
        onExportExcel: () => triggerExport({ ...activeFilters, report_key: reportKey }, 'XLSX'),
        threshold: 2000,
      });
    } else if (actionKey === 'excel') {
      await triggerExport({ ...activeFilters, report_key: reportKey }, 'XLSX');
    } else if (actionKey === 'pdf') {
      await triggerExport({ ...activeFilters, report_key: reportKey }, 'PDF');
    }
  };

  // ---------------------------------------------------------------------------
  // TOOL SELECT (Print → opens drawer, Export, Rollback)
  // ---------------------------------------------------------------------------

  const handleToolSelect = async (toolKey) => {
    if (toolKey === 'print' || toolKey === 'print-report') {
      setIsReportsDrawerOpen(true);
    } else if (toolKey === 'export_excel') {
      await triggerExport(activeFilters, 'XLSX');
    } else if (toolKey === 'export_pdf') {
      await triggerExport(activeFilters, 'PDF');
    } else if (toolKey === 'rollback') {
      const monthLabel = getMonthName(monthFilter);
      const confirmed = await appConfirm({
        title: 'Rollback Monthly Attendance',
        message: `Are you sure you want to rollback monthly attendance for ${monthLabel} ${yearFilter}? This will clear processed summary records. If salary has already been processed, rollback will be blocked.`,
        confirmText: 'Rollback Process',
        variant: 'danger',
        icon: 'history',
      });
      if (!confirmed) return;
      try {
        const res = await monthlyProcessApi.rollbackProcess({
          month: monthFilter,
          year: yearFilter,
          companyId: companyFilter || 0,
          branchId: branchFilter || 0,
          deptId: departmentFilter || 0,
        });
        appToast.success(res.message || `Monthly attendance for ${monthLabel} ${yearFilter} rolled back successfully.`);
        loadRecords();
      } catch (err) {
        const msg =
          err?.response?.data?.detail?.message ||
          (typeof err?.response?.data?.detail === 'string' ? err?.response?.data?.detail : null) ||
          err?.message ||
          'Failed to rollback monthly attendance.';
        appToast.error(msg);
      }
    }
  };

  return {
    state: {
      records,
      isLoading,
      isExporting,
      companyFilter,
      branchFilter,
      departmentFilter,
      employeeFilter,
      empGroupFilter,
      genderFilter,
      statusFilter,
      monthFilter,
      yearFilter,
      searchText,
      isReportsDrawerOpen,
      page,
      pageSize,
      totalCount,
      totalPages,
      // Options
      companyOptions: options.companies,
      branchOptions: options.branches,
      departmentOptions: options.departments,
      groupOptions: options.groups,
      genderOptions: options.genders,
      monthOptions: MONTH_OPTIONS,
      yearOptions: YEAR_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      tools: TOOLS_OPTIONS,
      predefinedReports: MONTHLY_ATTENDANCE_PREDEFINED_REPORTS,
    },
    actions: {
      setCompanyFilter,
      setBranchFilter,
      setDepartmentFilter,
      setEmployeeFilter,
      setEmpGroupFilter,
      setGenderFilter,
      setStatusFilter,
      setMonthFilter,
      setYearFilter,
      setSearchText,
      setIsReportsDrawerOpen,
      handleReportAction,
      handleStatusTabChange,
      setPage,
      handleSearch,
      resetFilters,
      handleDelete,
      handleToolSelect,
      refresh: loadRecords,
    },
  };
}
